/**
 * Tiny synchronous pub/sub keyed by `dbName::table`. Every successful write
 * (`put`/`update`/`delete`/`clear`/`tx`/`importAll`) calls {@link invalidate};
 * the React `useQuery` hook subscribes so SWR-style refetch happens without a
 * heavyweight cache layer. Deliberately framework-free so the core stays
 * importable without React.
 */

type Listener = () => void

const listeners = new Map<string, Set<Listener>>()

function channelKey(dbName: string, table: string): string {
	return `${dbName}::${table}`
}

/** Subscribe to writes on a `(dbName, table)` channel. Returns an unsub fn. */
export function subscribe(dbName: string, table: string, fn: Listener): () => void {
	const key = channelKey(dbName, table)
	let set = listeners.get(key)
	if (!set) {
		set = new Set()
		listeners.set(key, set)
	}
	set.add(fn)
	return () => {
		const s = listeners.get(key)
		if (!s) return
		s.delete(fn)
		if (s.size === 0) listeners.delete(key)
	}
}

/** Notify every listener on a `(dbName, table)` channel. Never throws. */
export function invalidate(dbName: string, table: string): void {
	const set = listeners.get(channelKey(dbName, table))
	if (!set) return
	for (const fn of Array.from(set)) {
		try {
			fn()
		} catch {
			// A subscriber throwing must not corrupt the write path.
		}
	}
}

/** Test helper — drop all subscriptions. */
export function __resetInvalidation(): void {
	listeners.clear()
}
